import { useState, useEffect, useMemo } from "react";
import { api } from "../api";
import {
  Loader2, Calendar, Clock, AlertTriangle, Building2,
  Stethoscope, ChevronDown, ChevronUp, Filter
} from "lucide-react";

interface WeekdayData {
  "Horas em falta Manhã"?: { value: number | null; raw: string };
  "Horas em falta Tarde"?: { value: number | null; raw: string };
  "Horas em falta Fim de tarde"?: { value: number | null; raw: string };
  "Horas em falta Total"?: { value: number | null; raw: string };
}

interface UnitData {
  weekdays: Record<string, WeekdayData>;
  summary: Record<string, { value: number | null; raw: string }>;
  schedule: string | null;
  medicos_dia: number | null;
  medicos_jul_ago: number | null;
  disp_h_sem: string | null;
}

interface Matrix {
  [specialty: string]: Record<string, UnitData>;
}

interface PriorityItem {
  specialty: string;
  unit: string;
  rank: number;
  metrics: Record<string, { value: number | null; raw: string }>;
  periodo_critico: string | null;
  prioridade: string | null;
}

const WEEKDAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta"];
const PERIOD_COLORS: Record<string, string> = {
  "Horas em falta Manhã": "bg-amber-50 text-amber-800",
  "Horas em falta Tarde": "bg-orange-50 text-orange-800",
  "Horas em falta Fim de tarde": "bg-red-50 text-red-800",
  "Horas em falta Total": "bg-slate-100 text-slate-800",
};

function fmtHours(v: number | null | undefined, raw?: string | null) {
  if (v === null || v === undefined) return raw || "—";
  return `${v}h`;
}

function priorityClass(p: string | null) {
  if (!p) return "text-slate-500";
  if (p === "Crítica") return "text-red-600 font-semibold";
  if (p === "Alta") return "text-orange-600 font-semibold";
  if (p === "Média") return "text-amber-600";
  return "text-slate-600";
}

export default function AgendaCoverage({ embedded }: { embedded?: boolean }) {
  const [summary, setSummary] = useState<{ specialties: string[]; units: string[]; weekdays: string[]; matrix: Matrix } | null>(null);
  const [priorities, setPriorities] = useState<{ items: PriorityItem[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>("");
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<"matrix" | "priorities">("matrix");

  useEffect(() => {
    setLoading(true);
    Promise.all([api.getAgendaCoverageSummary(), api.getAgendaCoveragePriorities()])
      .then(([sum, pri]) => {
        setSummary(sum);
        setPriorities(pri);
        const specs = sum.specialties || [];
        setSelectedSpecialty(specs[0] || "");
      })
      .catch((err) => console.error("Failed to load agenda coverage", err))
      .finally(() => setLoading(false));
  }, []);

  const units = useMemo(() => {
    if (!summary || !selectedSpecialty) return [];
    return Object.entries((summary.matrix || {})[selectedSpecialty] || {}).sort((a, b) => {
      const totalA = a[1].summary["Horas em falta Total"]?.value || 0;
      const totalB = b[1].summary["Horas em falta Total"]?.value || 0;
      return totalB - totalA;
    });
  }, [summary, selectedSpecialty]);

  const priorityItems = useMemo(() => {
    if (!priorities) return [];
    if (!selectedSpecialty) return priorities.items || [];
    return (priorities.items || []).filter((i) => i.specialty === selectedSpecialty);
  }, [priorities, selectedSpecialty]);

  const toggleUnit = (unit: string) => {
    const next = new Set(expandedUnits);
    if (next.has(unit)) next.delete(unit);
    else next.add(unit);
    setExpandedUnits(next);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading agenda coverage...
      </div>
    );
  }

  if (!summary || !summary.specialties.length) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900">Agenda Coverage</h1>
        <p className="text-slate-500 mt-2">No agenda coverage data available. Upload a file named like "Cobertura de Agendas ... .xlsx".</p>
      </div>
    );
  }

  const body = (
    <>
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Agenda Coverage</h1>
          <p className="text-slate-500 text-sm mt-1">
            Horas em falta por unidade e dia da semana · Agosto 2026
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-500">Specialty:</span>
        </div>
        <select
          value={selectedSpecialty}
          onChange={(e) => {
            setSelectedSpecialty(e.target.value);
            setExpandedUnits(new Set());
          }}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700"
        >
          {summary.specialties.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <div className="ml-auto flex items-center gap-2 bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab("matrix")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${activeTab === "matrix" ? "bg-white text-trofa-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
          >
            <Calendar className="w-4 h-4 inline mr-1" />
            Weekday matrix
          </button>
          <button
            onClick={() => setActiveTab("priorities")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${activeTab === "priorities" ? "bg-white text-trofa-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
          >
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            Priorities
          </button>
        </div>
      </div>

      {activeTab === "matrix" && (
        <div className="space-y-4">
          {units.map(([unit, data]) => {
            const total = data.summary["Horas em falta Total"]?.value;
            const isExpanded = expandedUnits.has(unit);
            return (
              <div key={unit} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  onClick={() => toggleUnit(unit)}
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-trofa-700" />
                    <span className="font-semibold text-slate-900">{unit}</span>
                    <span className="text-sm text-slate-500">
                      Total {fmtHours(total)}
                    </span>
                    {data.medicos_dia !== null && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                        {data.medicos_dia} médicos/dia
                      </span>
                    )}
                    {data.schedule && (
                      <span className="text-xs text-slate-500">
                        <Clock className="w-3 h-3 inline mr-0.5" />
                        {data.schedule}
                      </span>
                    )}
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </button>

                {isExpanded && (
                  <div className="px-5 pb-5">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium">Period</th>
                            {WEEKDAYS.map((d) => (
                              <th key={d} className="text-center px-3 py-2 font-medium">{d}</th>
                            ))}
                            <th className="text-center px-3 py-2 font-medium">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {["Horas em falta Manhã", "Horas em falta Tarde", "Horas em falta Fim de tarde"].map((metric) => (
                            <tr key={metric} className="border-t border-slate-100">
                              <td className="px-3 py-2 font-medium text-slate-700">{metric.replace("Horas em falta ", "")}</td>
                              {WEEKDAYS.map((d) => (
                                <td key={d} className="px-3 py-2 text-center">
                                  {fmtHours(data.weekdays[d]?.[metric]?.value, data.weekdays[d]?.[metric]?.raw)}
                                </td>
                              ))}
                              <td className="px-3 py-2 text-center font-semibold text-slate-800">
                                {fmtHours(data.summary[metric]?.value, data.summary[metric]?.raw)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      {data.medicos_jul_ago !== null && (
                        <span className="bg-slate-50 text-slate-600 px-2 py-1 rounded">
                          Médicos Jul+Ago: {data.medicos_jul_ago}
                        </span>
                      )}
                      {data.disp_h_sem && (
                        <span className="bg-slate-50 text-slate-600 px-2 py-1 rounded">
                          Disp. h/sem: {data.disp_h_sem}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activeTab === "priorities" && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-trofa-700" />
            <h2 className="font-semibold text-slate-900">Top 20 unidades com maior falta de cobertura</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">#</th>
                  <th className="text-left px-4 py-3 font-medium">Unit</th>
                  <th className="text-right px-4 py-3 font-medium">Manhã</th>
                  <th className="text-right px-4 py-3 font-medium">Tarde</th>
                  <th className="text-right px-4 py-3 font-medium">Fim de tarde</th>
                  <th className="text-right px-4 py-3 font-medium">Sábado</th>
                  <th className="text-right px-4 py-3 font-medium">Total/sem</th>
                  <th className="text-right px-4 py-3 font-medium">Médicos/dia</th>
                  <th className="text-left px-4 py-3 font-medium">Período crítico</th>
                  <th className="text-left px-4 py-3 font-medium">Prioridade</th>
                </tr>
              </thead>
              <tbody>
                {priorityItems.map((item) => {
                  const m = item.metrics;
                  return (
                    <tr key={`${item.specialty}-${item.unit}`} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-slate-500">{item.rank}</td>
                      <td className="px-4 py-3 font-medium text-slate-800">{item.unit}</td>
                      <td className="px-4 py-3 text-right">{fmtHours(m["Horas em falta Manhã"]?.value, m["Horas em falta Manhã"]?.raw)}</td>
                      <td className="px-4 py-3 text-right">{fmtHours(m["Horas em falta Tarde"]?.value, m["Horas em falta Tarde"]?.raw)}</td>
                      <td className="px-4 py-3 text-right">{fmtHours(m["Horas em falta Fim de tarde"]?.value, m["Horas em falta Fim de tarde"]?.raw)}</td>
                      <td className="px-4 py-3 text-right">{fmtHours(m["Horas em falta Sábado"]?.value, m["Horas em falta Sábado"]?.raw)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmtHours(m["Horas em falta Total"]?.value, m["Horas em falta Total"]?.raw)}</td>
                      <td className="px-4 py-3 text-right">{item.metrics["Médicos/dia"]?.value ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{item.periodo_critico || "—"}</td>
                      <td className={`px-4 py-3 ${priorityClass(item.prioridade)}`}>{item.prioridade || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return body;
  return <div className="p-6 max-w-7xl mx-auto">{body}</div>;
}
